import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IContratoCliente } from '@model/ContratoCliente';
import { useContratoClienteService } from '@services/ContratoClienteService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { ContratoClienteEditarFormulario } from './ContratoClienteEditarFormulario';

export type EditarContratoModalProps = {
    modal: IModalWindow
    contratoCliente: IContratoCliente
    onContratoClienteEditado: (contratoCliente: AxiosResponse<IContratoCliente>) => void
}

export function ContratoClienteEditarModal ({ modal, contratoCliente, onContratoClienteEditado }: EditarContratoModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const contratoClienteService = useContratoClienteService();

    const form =  useForm<Partial<IContratoCliente>>({
        inicio: useField(),
        valorPlano: useField(),
        valorAssessoria: useField(),
        franquiaGb: useField(),
        valorChip: useField(),
        valorChipDescontado: useField(),
        usuarioLinha: useField(),
        notas: useField()
    });

    const validation = useValidation<IContratoCliente>({
        valorPlano: yup.number().required(),
        valorAssessoria: yup.number().required(),
        franquiaGb: yup.number().required(),
        valorChipDescontado: yup.number().required()
    });

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();
            validation.validate(values)
                .then(() => {
                    loading.start();
                    contratoClienteService.editar(contratoCliente.contratoClienteId, values)
                        .then(response => {
                            message.success("Contrato editado com sucesso");
                            modal.close();
                            onContratoClienteEditado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);

        }
    }, [contratoCliente.contratoClienteId, contratoClienteService, form, loading, message, modal, onContratoClienteEditado, validation]);

    const handleEnter = React.useCallback(() => {
        if (!loading.isLoading) {
            loading.start();
            contratoClienteService.carregar(contratoCliente.contratoClienteId)
                .then(form.handleResponseSetValues)
                .catch(e => {
                    message.showException(e);
                    modal.close();
                })
                .finally(loading.stop);
        }
    }, [contratoCliente.contratoClienteId, contratoClienteService, form, loading, message, modal]);

    return (
        <ModalWindow 
            modal={modal} 
            size="md" 
            disabled={loading.isLoading}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle title={`Edição do Contrato ${contratoCliente.contratoClienteId}`} />
            
            <ModalWindowContent>
                <ContratoClienteEditarFormulario
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Editar Contrato</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}